import { useEffect, useState } from 'react';
import { Scan } from 'lucide-react';
import { useCanvasStore } from '../store/canvas-store';
import { isDesignFrameKind } from './chatTarget';
import { useReadOnly } from './readonly';
import { MIN_SIZE } from '../canvas/resizeRect';
import type { Command, Frame } from '../store/types';

/** Parse a numeric size/position input ("420", "420px", "420.6"). Null = junk
 *  (caller reverts the field to the live value). Pure — unit-tested. */
export function parseSizeValue(raw: string): number | null {
  const n = Number(raw.trim().replace(/px$/i, ''));
  return Number.isFinite(n) && raw.trim() !== '' ? Math.round(n) : null;
}

type RectPatch = Partial<{ x: number; y: number; w: number; h: number }>;

/** Merge an edited field into the frame's full rect as one frame.resize.
 *  `measuredH` supplies the height for auto-height frames (frame.h undefined).
 *  Returns null when the rect is unchanged so callers skip the dispatch (and
 *  the undo entry). Pure — unit-tested. */
export function buildResize(
  frame: Frame,
  patch: RectPatch,
  measuredH: number,
): Extract<Command, { t: 'frame.resize' }> | null {
  const cur = { x: frame.x, y: frame.y, w: frame.w, h: frame.h ?? measuredH };
  const next = {
    x: patch.x ?? cur.x,
    y: patch.y ?? cur.y,
    w: Math.max(MIN_SIZE, patch.w ?? cur.w),
    h: Math.max(MIN_SIZE, patch.h ?? cur.h),
  };
  if (
    Object.keys(patch).length === 0 ||
    (next.x === cur.x && next.y === cur.y && next.w === cur.w && next.h === cur.h)
  ) {
    return null;
  }
  return { t: 'frame.resize', id: frame.id, ...next };
}

/** Live height of the frame's root element in canvas units (rect is in screen
 *  px on both render paths — the unified path syncs transforms — so divide by
 *  zoom). Fallback: the frame's stored height or MIN_SIZE. */
function measureFrameHeight(frameId: string, zoom: number, stored?: number): number {
  const el = document.querySelector(`[data-frame-id="${frameId}"]`);
  if (el) {
    const h = el.getBoundingClientRect().height / (zoom || 1);
    if (h > 0) return Math.round(h);
  }
  return stored ?? MIN_SIZE;
}

/** Natural content height: the root's scrollHeight (overflow included even
 *  under overflow:hidden), in layout px — layoutsubtree children are unscaled,
 *  so no zoom correction. */
function measureContentHeight(frameId: string): number | null {
  const el = document.querySelector(`[data-frame-id="${frameId}"]`) as HTMLElement | null;
  if (!el) return null;
  const h = el.scrollHeight;
  return h > 0 ? Math.round(h) : null;
}

const FIELDS = ['x', 'y', 'w', 'h'] as const;
type Field = (typeof FIELDS)[number];

/**
 * Compact X/Y/W/H + fit-to-content strip for the single selected design frame
 * (the deferred C.3 "small properties surface"). Fixed above the ActionChip so
 * it needs no zoom math; values commit on Enter/blur, Esc reverts the field.
 * One dispatch per commit = one undo step.
 */
export function SizeInspector() {
  const selection = useCanvasStore((s) => s.selection);
  const frames = useCanvasStore((s) => s.doc.frames);
  const zoom = useCanvasStore((s) => s.doc.viewport.zoom) || 1;
  const dispatch = useCanvasStore((s) => s.dispatch);
  const readOnly = useReadOnly();

  const frame =
    selection.length === 1 ? frames.find((f) => f.id === selection[0]) : undefined;
  const target = frame && isDesignFrameKind(frame.kind) ? frame : undefined;

  // Draft values while typing; null = mirror the live frame.
  const [drafts, setDrafts] = useState<Partial<Record<Field, string>>>({});
  useEffect(() => setDrafts({}), [target?.id]);

  if (!target || readOnly) return null;

  const live: Record<Field, number | ''> = {
    x: Math.round(target.x),
    y: Math.round(target.y),
    w: Math.round(target.w),
    h: target.h !== undefined ? Math.round(target.h) : '',
  };

  const commit = (field: Field, raw: string) => {
    setDrafts((d) => ({ ...d, [field]: undefined }));
    const v = parseSizeValue(raw);
    if (v === null) return; // junk → field snaps back to live value
    const cmd = buildResize(target, { [field]: v }, measureFrameHeight(target.id, zoom, target.h));
    if (cmd) dispatch(cmd);
  };

  const fitToContent = () => {
    const h = measureContentHeight(target.id);
    if (h === null) return;
    const cmd = buildResize(target, { h }, measureFrameHeight(target.id, zoom, target.h));
    if (cmd) dispatch(cmd);
  };

  const input = (field: Field, label: string) => (
    <label key={field} className="flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
      <span className="select-none w-3 text-right">{label}</span>
      <input
        value={drafts[field] ?? String(live[field])}
        placeholder={field === 'h' ? 'auto' : undefined}
        onChange={(e) => setDrafts((d) => ({ ...d, [field]: e.target.value }))}
        onBlur={(e) => commit(field, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setDrafts((d) => ({ ...d, [field]: undefined }));
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="w-14 px-1.5 py-0.5 rounded-[6px] text-xs text-[var(--color-text)] bg-[var(--color-surface-2)] border border-[var(--color-border)] focus:border-[var(--color-accent)] outline-none"
      />
    </label>
  );

  return (
    <div
      data-size-inspector
      className="fixed bottom-[64px] left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] shadow-[var(--shadow-1)]"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {input('x', 'X')}
      {input('y', 'Y')}
      {input('w', 'W')}
      {input('h', 'H')}
      <button
        type="button"
        title="Fit height to content"
        onClick={fitToContent}
        className="flex items-center justify-center w-6 h-6 rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)] hover:text-[var(--color-text)]"
      >
        <Scan size={13} />
      </button>
    </div>
  );
}
